/** @format */

import { useState, useCallback } from 'react';
import { SaleSubmissionFormData, GeneralQuestions } from '../types';

const INITIAL_FORM_DATA: SaleSubmissionFormData = {
  request: false,
  AOR: '',
  NPN: '',
  healthSherpa: '',
} as SaleSubmissionFormData;

const INITIAL_GENERAL_Q: GeneralQuestions = {
  dependents: false,
  status: 'single',
};

export const useFormData = () => {
  const [formData, setFormData] = useState<SaleSubmissionFormData>(INITIAL_FORM_DATA);
  const [generalQ, setGeneralQ] = useState<GeneralQuestions>(INITIAL_GENERAL_Q);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    if (type === 'checkbox' && name === 'planType') {
      setFormData((prevState) => {
        const currentPlanType = prevState.planType || [];
        let updatedPlanType = [...currentPlanType];
        if (prevState.request && value === 'Major Medical') {
          if (!updatedPlanType.includes('Major Medical')) {
            updatedPlanType.push('Major Medical');
          }
        } else {
          updatedPlanType = checked
            ? [...currentPlanType, value]
            : currentPlanType.filter((type) => type !== value);
        }
        return {
          ...prevState,
          planType: updatedPlanType,
        };
      });
    } else if (type === 'radio' && name === 'request') {
      const isAcaRequest = value === 'true';
      setFormData((prevState) => ({
        ...prevState,
        request: isAcaRequest,
        planType: isAcaRequest
          ? [...(prevState.planType || []), 'Major Medical']
          : (prevState.planType || []).filter((type) => type !== 'Major Medical'),
      }));
    } else if (name.includes('.')) {
      const [section, key] = name.split('.');
      setFormData((prevState) => ({
        ...prevState,
        [section]: {
          ...(prevState[section as keyof SaleSubmissionFormData] as Record<string, any>),
          [key]: type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  }, []);

  const handleGeneralQChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setGeneralQ((prevGeneralQ) => ({
      ...prevGeneralQ,
      [name]: value === 'true' ? true : value === 'false' ? false : value,
    }));
  }, []);

  const handleAncillaryChange = useCallback((index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const field = name.split('-')[0];
    
    setFormData((prevState) => {
      const currentAncillaries = prevState.ancillaries || [];
      const newAncillaries = currentAncillaries.map((ancillary, i) => {
        if (index === i) {
          return {
            ...ancillary,
            [field]: value === 'true' ? true : value === 'false' ? false : value,
          };
        }
        return ancillary;
      });
      return { ...prevState, ancillaries: newAncillaries };
    });
  }, []);

  const handleDependentChange = useCallback((index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData((prevState) => ({
      ...prevState,
      dependentsInfo: (prevState.dependentsInfo || []).map((dependent, i) =>
        i === index ? { ...dependent, [name]: value } : dependent
      ),
    }));
  }, []);

  const addAncillary = useCallback(() => {
    setFormData((prevState) => ({
      ...prevState,
      ancillaries: [
        ...(prevState.ancillaries || []),
        {
          carrier: '',
          isPostDate: null,
          billingDate: '',
          premium: '',
          status: '',
        },
      ],
    }));
  }, []);

  const removeAncillary = useCallback((index: number) => {
    setFormData((prevState) => ({
      ...prevState,
      ancillaries: (prevState.ancillaries || []).filter((_, i) => i !== index),
    }));
  }, []);

  const addDependent = useCallback(() => {
    setFormData((prevState) => ({
      ...prevState,
      dependentsInfo: [
        ...(prevState.dependentsInfo || []),
        {
          firstName: '',
          lastName: '',
          dob: '',
          ssn: '',
        },
      ],
    }));
  }, []);

  const removeDependent = useCallback((index: number) => {
    setFormData((prevState) => ({
      ...prevState,
      dependentsInfo: (prevState.dependentsInfo || []).filter((_, i) => i !== index),
    }));
  }, []);

  const handlePlanTypeChange = useCallback((value: string, checked: boolean) => {
    setFormData((prevState) => {
      const currentPlanType = prevState.planType || [];
      let updatedPlanType = [...currentPlanType];
      if (prevState.request && value === 'Major Medical') {
        if (!updatedPlanType.includes('Major Medical')) {
          updatedPlanType.push('Major Medical');
        }
      } else {
        updatedPlanType = checked
          ? [...currentPlanType, value]
          : currentPlanType.filter((type) => type !== value);
      }
      return {
        ...prevState,
        planType: updatedPlanType,
      };
    });
  }, []);

  const handleRequestChange = useCallback((isAcaRequest: boolean) => {
    setFormData((prevState) => ({
      ...prevState,
      request: isAcaRequest,
      planType: isAcaRequest
        ? [...(prevState.planType || []), 'Major Medical']
        : (prevState.planType || []).filter((type) => type !== 'Major Medical'),
    }));
  }, []);

  const handleAddressChange = useCallback((field: string, value: any) => {
    setFormData((prevState) => ({
      ...prevState,
      address: {
        ...prevState.address,
        [field]: value,
      },
    }));
  }, []);

  const handleSpouseChange = useCallback((field: string, value: any) => {
    setFormData((prevState) => ({
      ...prevState,
      spouseInfo: {
        ...prevState.spouseInfo,
        [field]: value,
      },
    }));
  }, []);

  const handleMajorMedChange = useCallback((field: string, value: any) => {
    setFormData((prevState) => ({
      ...prevState,
      majorMed: {
        ...prevState.majorMed,
        [field]: value,
      },
    }));
  }, []);

  const handlePrivateMedChange = useCallback((field: string, value: any) => {
    setFormData((prevState) => ({
      ...prevState,
      privateMed: {
        ...prevState.privateMed,
        [field]: value,
      },
    }));
  }, []);

  const handleAncillaryFieldChange = useCallback((index: number, field: string, value: any) => {
    setFormData((prevState) => {
      const currentAncillaries = prevState.ancillaries || [];
      const newAncillaries = currentAncillaries.map((ancillary, i) => {
        if (index === i) {
          return {
            ...ancillary,
            [field]: value,
          };
        }
        return ancillary;
      });
      return { ...prevState, ancillaries: newAncillaries };
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setGeneralQ(INITIAL_GENERAL_Q);
  }, []);

  return {
    formData,
    generalQ,
    handleChange,
    handleGeneralQChange,
    handlePlanTypeChange,
    handleRequestChange,
    handleAddressChange,
    handleSpouseChange,
    handleAncillaryChange,
    handleAncillaryFieldChange,
    handleDependentChange,
    handleMajorMedChange,
    handlePrivateMedChange,
    addAncillary,
    removeAncillary,
    addDependent,
    removeDependent,
    resetForm,
    setFormData,
    setGeneralQ,
  };
};
